import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import styled from 'styled-components';

import BlockWrapper from './shared/BlockWrapper';
import utils from '../../../common/utils';
import fabric from '../../../common/fabric';
import variables from '../../../common/variables';

const CopyElement = styled.button`
  position: absolute;
  top: ${props => `${props.scale * 15}px`};
  right: ${props => `${props.scale * 15}px`};
  display: block;
  min-width: 36px;
  max-width: 100%;
  height: 25px;
  line-height: 23px;
  color: ${variables.colors.white};
  border: 1px solid transparent;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  padding: 0 12.5px;
  border-radius: 12.5px;
  white-space: nowrap;
  transform: ${props => `scale(${props.scale})`};
  transform-origin: right top;
  opacity: ${props => (props.visible ? '1' : '0')};
  z-index: ${props => (props.visible ? '20' : '-1')};
  transition: all 0.2s ease-in-out;
`;

const CopyButton = styled(CopyElement)`
  background-color: ${variables.colors.blue};

  &:hover {
    background-color: ${variables.colors.highlightBlue};
  }
`;

const Copied = styled(CopyElement)`
  background-color: ${variables.colors.black};
`;

const Copy = props =>
  props.copied ? (
    <Copied scale={props.scale} visible={props.visible}>
      Copied!
    </Copied>
  ) : (
    <CopyButton
      onClick={e => {
        e.stopPropagation();
        props.copy();
      }}
      scale={props.scale}
      visible={props.visible}
    >
      Copy code
    </CopyButton>
  );
Copy.propTypes = {
  visible: PropTypes.bool.isRequired,
  copied: PropTypes.bool.isRequired,
  scale: PropTypes.number.isRequired,
  copy: PropTypes.func.isRequired,
};

const CodeImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.opacity};
`;
CodeImage.propTypes = {
  src: PropTypes.string.isRequired,
  opacity: PropTypes.number.isRequired,
};

class Code extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mouseOver: false,
      copied: false,
    };
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  @autobind
  onMouseEnter() {
    this.setState({ mouseOver: true });
  }

  @autobind
  onMouseLeave() {
    this.setState({ mouseOver: false });
  }

  @autobind
  copy() {
    utils.copy(this.props.params.ludus.code);
    this.setState({ copied: true });
    this.timeout = setTimeout(() => {
      this.setState({ copied: false });
    }, 1000);
  }

  render() {
    const { params, hyperlink, style, matrixGroup, globalScale } = this.props;

    const groupMatrix = this.props.matrixGroup
      ? fabric.util.multiplyTransformMatrices(
          matrixGroup,
          params.ludus.transformMatrix,
        )
      : params.ludus.transformMatrix;

    const transformMatrix = fabric.util.multiplyTransformMatrices(
      [globalScale, 0, 0, globalScale, 0, 0],
      groupMatrix,
    );

    const innerStyles = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      transform: utils.getTransformCSSFromObject(params),
    };

    return (
      <BlockWrapper
        globalCompositeOperation={params.globalCompositeOperation}
        groupAngle={this.props.groupAngle}
        localScale={this.props.params.scaleX / this.props.groupScaleX}
        shadow={params.shadow || null}
        hyperlink={hyperlink}
        style={{
          ...style,
          left: 0,
          top: 0,
          transformOrigin: 'center center',
          transform: `translate(-50%, -50%) matrix(${transformMatrix})`,
        }}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <div style={innerStyles}>
          <CodeImage
            src={params.ludus.imageUrl}
            opacity={this.props.groupOpacity}
          />
          <Copy
            visible={this.state.mouseOver}
            copied={this.state.copied}
            scale={1 / this.props.globalScale}
            copy={this.copy}
          />
        </div>
      </BlockWrapper>
    );
  }
}

Code.propTypes = {
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  globalScale: PropTypes.number.isRequired,
  groupAngle: PropTypes.number.isRequired,
  groupOpacity: PropTypes.number.isRequired,
  groupScaleX: PropTypes.number.isRequired,
  hyperlink: PropTypes.string,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
};
Code.defaultProps = {
  hyperlink: null,
  matrixGroup: null,
};

export default Code;
